/**
 * Google Analytics (GA4) Service with Google Consent Mode v2
 */

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

let isScriptInjected = false;

/**
 * Ensure dataLayer and gtag are available on window
 */
export function ensureGtag(): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer?.push(arguments);
    };
  }
}

/**
 * Configure default Consent Mode v2 state (default: all tracking denied until user accepts)
 */
export function setDefaultConsent(): void {
  if (typeof window === 'undefined') return;
  ensureGtag();

  window.gtag?.('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    wait_for_update: 500,
  });
}

/**
 * Dynamically load Google Analytics script and initialize tracking
 */
export function enableGoogleAnalytics(measurementId: string): void {
  if (typeof window === 'undefined' || !measurementId) return;

  // Un-disable GA if it was previously disabled
  window[`ga-disable-${measurementId}`] = false;

  ensureGtag();

  // Update Consent Mode v2 to granted for analytics
  window.gtag?.('consent', 'update', {
    analytics_storage: 'granted',
  });

  // Inject gtag.js script if not already present
  if (!isScriptInjected && !document.getElementById('ga-gtag-script')) {
    const script = document.createElement('script');
    script.id = 'ga-gtag-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
    isScriptInjected = true;
  }

  // Initialize gtag config
  window.gtag?.('js', new Date());
  window.gtag?.('config', measurementId, {
    send_page_view: true,
    anonymize_ip: true,
  });
}

/**
 * Revoke Google Analytics tracking, disable GA, and clean tracking cookies
 */
export function disableGoogleAnalytics(measurementId: string): void {
  if (typeof window === 'undefined') return;

  if (measurementId) {
    window[`ga-disable-${measurementId}`] = true;
  }

  ensureGtag();

  // Update Consent Mode v2 to denied for analytics
  window.gtag?.('consent', 'update', {
    analytics_storage: 'denied',
  });

  // Remove GA cookies (_ga, _ga_*, etc.)
  clearGoogleAnalyticsCookies();
}

/**
 * Clears GA cookies (_ga, _gid, _gat, _ga_*) across current host and root domains
 */
export function clearGoogleAnalyticsCookies(): void {
  if (typeof document === 'undefined') return;

  const cookies = document.cookie.split(';');
  const domainParts = window.location.hostname.split('.');

  for (const cookie of cookies) {
    const cookieName = cookie.split('=')[0].trim();
    if (cookieName === '_ga' || cookieName === '_gid' || cookieName === '_gat' || cookieName.startsWith('_ga_')) {
      // Clear for current path and host
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      // Clear for parent domain if applicable
      if (domainParts.length >= 2) {
        const rootDomain = `.${domainParts.slice(-2).join('.')}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain};`;
      }
    }
  }
}

/**
 * Custom Event Tracking Helper
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, string | number | boolean | undefined>
): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', eventName, eventParams);
}
